import {
   addDays,
   addMonths,
   eachYearOfInterval,
   endOfMonth,
   format,
   formatISO,
   getMonth,
   getQuarter,
   intervalToDuration,
   isDate,
   isSameQuarter,
   isValid,
   parseISO,
   setDayOfYear,
   setMonth,
   startOfYear,
} from 'date-fns';
import type { IntlShape } from 'react-intl';
import { AggregationLevel, type AggregationLevelEnum } from 'utils/enum';

export function convertDatesToUTC<T extends Record<string, any>>(dateObj: T): T {
   return Object.fromEntries(
      Object.entries(dateObj).map(([key, value]) => [key, isValid(value) && isDate(value) ? formatISO(value, { representation: 'date' }) : value])
   ) as T;
}

export function parseDateValue(value: unknown) {
   if (!value) return null;

   const date = value instanceof Date ? value : typeof value === 'string' ? parseISO(value) : null;
   return date && isValid(date) ? date : null;
}

export function endOfHalfYear(date: Date): Date {
   const y = startOfYear(date);
   return getMonth(date) <= 5 ? endOfMonth(setMonth(y, 5)) : endOfMonth(setMonth(y, 11));
}

export function getHalfYear(date: Date): number {
   return getQuarter(date) <= 2 ? 1 : 2;
}

export function eachHalfYearOfInterval(interval: Interval) {
   const duration = intervalToDuration({ start: interval.start, end: addDays(interval.end, 1) });

   if (!duration.years && (!duration.months || duration.months < 6)) {
      return [setDayOfYear(interval.start, 1)];
   } else if (!duration.years && duration.months && duration.months >= 6) {
      const firstDayOfYear = startOfYear(interval.start);

      return [firstDayOfYear, addMonths(firstDayOfYear, 6)];
   } else if (duration.years) {
      return eachYearOfInterval(interval).flatMap((year) => [year, addMonths(year, 6)]);
   }

   // Explicit return for any uncaught edge cases
   return [];
}

export function friendlyFormatDateTimeRange(from: Date, to: Date): string | undefined {
   const duration = intervalToDuration({ start: from, end: addDays(to, 1) });
   if (!duration) {
      return undefined;
   }

   const [unevenPart = '', unevenValue] = Object.entries(duration).find(([, value]) => value !== 0) ?? [];

   if (unevenPart === 'years' && unevenValue === 1) {
      return format(to, 'yyyy');
   }

   if (!['years', 'months'].includes(unevenPart)) {
      return undefined;
   }

   if (Object.values(duration).filter((value) => value === 0).length !== Object.values(duration).length - 1) {
      return undefined;
   }

   if (unevenPart === 'months') {
      switch (unevenValue) {
         case 1:
            return format(to, 'MM yyyy');
         case 3:
            return isSameQuarter(from, to) ? format(to, 'QQQ yyyy') : undefined;
         case 6:
            return [0, 6].includes(getMonth(from)) ? `H${getHalfYear(from)} ${from.getFullYear()}` : undefined;
         default:
            return undefined;
      }
   }

   return undefined;
}

interface RelativeTimeInterval {
   value: number;
   unit: Intl.RelativeTimeFormatUnit;
}

function getRelativeTimeInterval(date: Date): RelativeTimeInterval {
   const {
      years = 0,
      months = 0,
      days = 0,
      hours = 0,
      minutes = 0,
      seconds = 0,
   } = intervalToDuration({
      start: date,
      end: new Date(),
   });

   let value: number;
   let unit: Intl.RelativeTimeFormatUnit;

   if (years >= 1) {
      unit = 'year';
      value = years;
   } else if (years < 1 && months > 0) {
      value = months;
      unit = 'month';
   } /* else if (interval.months && interval.months < 1 && (interval?.weeks ?? 0) > 0) {
      value = interval?.weeks ?? 0;
      unit = 'week';
   } */ else if (months < 1 && days > 0) {
      value = days;
      unit = 'day';
   } else if (days < 1 && hours > 0) {
      value = hours;
      unit = 'hour';
   } else if (hours < 1 && minutes > 0) {
      value = minutes;
      unit = 'minute';
   } else {
      value = seconds || 0;
      unit = 'second';
   }

   return {
      value,
      unit,
   };
}

function getRelativeTimeIntervalFromNow(date: Date): RelativeTimeInterval {
   const now = new Date();
   const diffMs = date.getTime() - now.getTime();
   const start = diffMs >= 0 ? now : date;
   const end = diffMs >= 0 ? date : now;
   const {
      years = 0,
      months = 0,
      days = 0,
      hours = 0,
      minutes = 0,
      seconds = 0,
   } = intervalToDuration({
      start,
      end,
   });

   let value: number;
   let unit: Intl.RelativeTimeFormatUnit;

   if (years >= 1) {
      unit = 'year';
      value = years;
   } else if (months > 0) {
      unit = 'month';
      value = months;
   } else if (days > 0) {
      unit = 'day';
      value = days;
   } else if (hours > 0) {
      unit = 'hour';
      value = hours;
   } else if (minutes > 0) {
      unit = 'minute';
      value = minutes;
   } else {
      unit = 'second';
      value = seconds || 0;
   }

   const sign = diffMs < 0 ? -1 : 1;
   const signedValue = value === 0 ? 0 : value * sign;

   return {
      value: signedValue,
      unit,
   };
}

export function getRelativeTime(intl: IntlShape, date: Date, options: Intl.RelativeTimeFormatOptions = { numeric: 'auto' }): string {
   const interval = getRelativeTimeInterval(date);

   return intl.formatRelativeTime(-interval.value, interval.unit, { style: 'narrow', ...options });
}

export function getRelativeTimeFromNow(intl: IntlShape, date: Date, options: Intl.RelativeTimeFormatOptions = { numeric: 'auto' }): string {
   const interval = getRelativeTimeIntervalFromNow(date);

   return intl.formatRelativeTime(interval.value, interval.unit, { style: 'long', ...options });
}

export function aggregationLevelFromPeriod(from: Date, to: Date): AggregationLevelEnum {
   const duration = intervalToDuration({ start: from, end: addDays(to, 1) });

   if (
      duration.years === 1 &&
      Object.entries(duration)
         .filter(([key]) => !['years'].includes(key))
         .every(([, value]) => value === 0)
   ) {
      return AggregationLevel.YEAR;
   } else if (
      duration.years === 0 &&
      duration.months === 6 &&
      Object.entries(duration)
         .filter(([key]) => !['years', 'months'].includes(key))
         .every(([, value]) => value === 0)
   ) {
      return AggregationLevel.HALF_YEAR;
   } else if (
      duration.months === 3 &&
      Object.entries(duration)
         .filter(([key]) => !['years', 'months'].includes(key))
         .every(([, value]) => value === 0)
   ) {
      return AggregationLevel.QUARTER;
   } else if (
      duration.months === 1 &&
      Object.entries(duration)
         .filter(([key]) => !['years', 'months'].includes(key))
         .every(([, value]) => value === 0)
   ) {
      return AggregationLevel.MONTH;
   } else {
      return AggregationLevel.AUTOMATIC;
   }
}
